import Icon from '@/components/snippets/icon/icon'
import { useEffect, useRef, useState } from 'preact/hooks'
import { useOmneoClient } from '../hooks/useOmneoClient'
import { Suspense } from './Suspense'
import { useRefetch } from '../hooks/useRefetch'
import { Checkbox, GALLERY_LIMIT, imageGallery, WishListRow } from './AccountWishlistPDP'
import { getAllWishlists } from '../helpers/getAllWishlists'
import LoaderSpin from '@/components/snippets/loader-spin/loader-spin'
import { useFormInput } from '../hooks/useFormInput'
import { accountTranslations } from '../helpers/translations'
/**
 * Component AccountWishlistModal
 * DOM Element: x-account-wishlist-modal
 *
 * This element is used when a customer is logged in and we need to pull
 * the wishlist data form the customer's Lists attribute from Omneo
 * and not local storage.
 *
 */
/**
 *
 * @param {{children: Readonly<React.ReactNode>}} param0
 * @returns
 */
export function AccountWishlistModal({ children, props }) {
  const [isLoading, setIsLoading] = useState(false)
  const [config, setConfig] = useState({
    state: 'SELECT_LIST',
    wishlists: [],
    selectedVariantSku: props.variantKeys[props.config.variantId],
    hasChange: false
  })

  const formRef = useRef(null)

  /**
   *
   * @param {'SELECT_LIST' | 'CREATE_LIST'} state
   */
  const handleStateUpdate = (state) => {
    setConfig((c) => ({
      ...c,
      state
    }))

    if (state === 'CREATE_LIST') {
      setTimeout(() => {
        document.querySelector('.new-list-container')?.scrollIntoView({ behavior: 'instant' })
        document.querySelector('.new-list-container input[name="list_name"]')?.focus()
      }, 100)
    }
  }

  const { refetch } = useRefetch()
  const { client } = useOmneoClient()
  if (!client) {
    return <></>
  }

  const handleDataLoad = async (refetch = false) => {
    try {
      const list = await getAllWishlists(client, !refetch)
      setConfig((c) => ({ ...c, wishlists: [...list], state: 'SELECT_LIST' }))
      document.dispatchEvent(new Event('omneo:wishlist:update'))
    } catch (err) {
      console.log(err)
    }
  }

  const handleChange = () => {
    const data = new FormData(formRef.current)
    let hasChange = false

    data.forEach((value, key) => {
      if (key.startsWith('wishlist_') && value === 'true') {
        hasChange = true
      }
    })

    setConfig((c) => ({
      ...c,
      hasChange
    }))
  }

  return (
    <>
      <AccountWishListModalHeader title={accountTranslations.wishlist.add_to_wishlist} handleOpen={props.handleOpen} />
      <div className={'relative h-full flex-1 overflow-y-auto'}>
        <Suspense
          refetch={refetch}
          props={{
            asyncCallback: handleDataLoad
          }}
        >
          <form
            ref={formRef}
            className={'flex h-full flex-col'}
            onSubmit={async (e) => {
              e.preventDefault()
              setIsLoading(true)
              const data = new FormData(e.currentTarget)
              const selectedLists = []
              let createdList

              if (config.state === 'CREATE_LIST') {
                const name = data.get('list_name')
                createdList = await client.createWishlist({ name })
                selectedLists.push({ id: createdList.id, selected: true, profileId: null })
              } else {
                data.forEach((value, key) => {
                  const existing = selectedLists.find((list) => list.id === key.split('_')[1])
                  if (key.startsWith('wishlist_')) {
                    if (existing) {
                      existing.selected = value === 'true'
                    } else {
                      selectedLists.push({ id: key.split('_')[1], selected: value === 'on', profileId: null })
                    }
                  } else if (key.startsWith('profile_')) {
                    if (existing) {
                      existing.profileId = value
                    } else {
                      selectedLists.push({ id: key.split('_')[1], selected: false, profileId: value })
                    }
                  }
                })
              }

              const sku = config.selectedVariantSku
              const image = props.config.img
              const productId = props.result.uid
              const variantId = props.result.variants.find((v) => v.sku === sku)?.id

              const wishlistActions = []

              for (const selectedList of selectedLists) {
                const list =
                  config.state === 'CREATE_LIST'
                    ? createdList
                    : config.wishlists.find((list) => list.id.toString() === selectedList.id)
                if (!list) continue

                let profile = client.profileClient
                if (selectedList.profileId) {
                  client.client.profile.Connection(Number(selectedList.profileId))
                  profile = client
                }

                const items = list.items
                const item = items.find((item) => item.product_variant?.sku === sku)

                if (!item && selectedList.selected) {
                  // If the item isn't in the list but is selected push the item to the list
                  const addRes = await profile.addWishlistItem(selectedList.id, sku, {
                    img: image,
                    selected: sku,
                    productId,
                    variantId
                  })
                  wishlistActions.push({
                    list: selectedList,
                    action: 'add',
                    item: addRes
                  })
                } else if (item && !selectedList.selected) {
                  // If the item is found in the list but no longer selected, remove item from list.
                  await profile.removeWishlistItem(list.id, item.id)
                }
              }

              document.dispatchEvent(new CustomEvent('omneo:wishlist:update', { detail: { useCache: false, wishlistActions } }))
              setIsLoading(false)
              handleStateUpdate('SELECT_LIST')
              document.querySelector('div[data-modal-parent="wishlist"]').click()
            }}
          >
            <div className={'flex flex-1 flex-col gap-6 overflow-y-auto p-6'}>
              {config.wishlists?.map((wishlist) => (
                <WishlistRow
                  wishlist={wishlist}
                  sku={config.selectedVariantSku}
                  isLoading={isLoading}
                  isCreateList={config.state === 'CREATE_LIST'}
                  handleChange={handleChange}
                />
              ))}
              {config.state === 'CREATE_LIST' && (
                <AccountWishListModalNewList
                  isLoading={isLoading}
                  cancelCreateList={() => handleStateUpdate('SELECT_LIST')}
                />
              )}
            </div>
            <AccountWishListModalButton
              config={config}
              handleStateUpdate={handleStateUpdate}
              isLoading={isLoading}
              hasChange={config.hasChange}
            />
          </form>
        </Suspense>
      </div>
    </>
  )
}

const WishlistRow = ({ wishlist, sku, isLoading, isCreateList, handleChange }) => {
  const images = imageGallery(wishlist.items, GALLERY_LIMIT)
  const [selected, setSelected] = useState(wishlist.items.some((i) => String(i.meta?.selected) === String(sku)))

  useEffect(() => {
    handleChange()
  }, [selected])

  return (
    <div className={'flex flex-col gap-2 border-b border-neutrals-light-grey pb-6'}>
      <div className={'flex flex-row items-center gap-2'}>
        <input type="hidden" name={`profile_${wishlist.id}`} value={wishlist.profile?.id} />
        <Checkbox
          isChecked={selected}
          name={`wishlist_${wishlist.id}`}
          handleIsChecked={(e) => {
            setSelected((s) => !s)
          }}
          disabled={isLoading || isCreateList}
        />
        <div className={'flex items-center gap-2'}>
          {!!wishlist.profile?.connector?.avatar_url && (
            <img className={'h-5.5 w-5.5 rounded-full'} src={wishlist.profile.connector.avatar_url} />
          )}
          <div>{wishlist.name}</div>
        </div>
      </div>
      <div className={'flex gap-1'}>
        {images.map((i, index) => {
          return (
            <div className={'relative aspect-1 flex-1 overflow-hidden rounded'}>
              <img src={i.img} className={'h-full w-full object-cover object-bottom'} />
              {wishlist.items.length > 4 && index === 3 && (
                <div className={'absolute inset-0 flex items-center justify-center bg-black/60'}>
                  <div className={'paragraph-lg font-bold text-white'}>+{wishlist.items.length - 3}</div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function AccountWishListModalNewList({ isLoading, cancelCreateList }) {
  const { formInputs, handleOnChange } = useFormInput({
    list_name: ''
  })
  return (
    <>
      <WishListRow isNewList={true} isCreateList={true}>
        <div className="flex items-center w-full">
          <div className="flex-1">
            <input
              name={formInputs.list_name.key}
              value={formInputs.list_name.value}
              onChange={handleOnChange}
              className="w-full !border-none !outline-none !ring-0"
              placeholder={accountTranslations.wishlist.name_your_list}
              disabled={isLoading}
            />
          </div>
          {!isLoading && (
            <div className="flex items-center gap-2">
              <button type="button" onClick={cancelCreateList} disabled={isLoading}>
                <Icon id="close" className="text-primary-black" />
              </button>
              <button type="submit" disabled={isLoading}>
                <Icon id="tick" className="text-primary-black" />
              </button>
            </div>
          )}
        </div>
      </WishListRow>
    </>
  )
}

function AccountWishListModalButton({ config, handleStateUpdate, isLoading, hasChange }) {
  const isCreateList = config.state === 'CREATE_LIST'
  return (
    <div className={'bg-neutrals-base p-6'}>
      <div className={'flex w-full flex-row justify-between gap-2'}>
        <button
          type="button"
          onClick={() => {
            handleStateUpdate('CREATE_LIST')
          }}
          className={'btn btn-md btn-outline-black relative w-full overflow-hidden'}
          disabled={isLoading || isCreateList}
        >
          {isLoading && isCreateList ? <LoaderSpin wrapperClass="bg-black" /> : accountTranslations.wishlist.create_wishlist}
        </button>
        <button
          type="submit"
          disabled={config.state === 'CREATE_LIST' || isLoading || !hasChange}
          className={'btn btn-md btn-solid-black relative w-full overflow-hidden'}
        >
          {isLoading && !isCreateList ? <LoaderSpin wrapperClass="bg-black" /> : accountTranslations.wishlist.add_to_wishlist}
        </button>
      </div>
    </div>
  )
}

function AccountWishListModalHeader({ title, handleOpen }) {
  return (
    <div class="sticky top-0 z-20 flex items-center justify-between border-b border-neutrals-light-grey bg-primary-white  py-4 pl-6 pr-4">
      <h4 class="h4">{title}</h4>
      <button data-modal-parent="wishlist" data-modal-role="close" className={'-m-4 p-4'} onClick={handleOpen}>
        <Icon id="close" class="min-h-6 min-w-6 cursor-pointer" />
      </button>
    </div>
  )
}
